import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom';
import { AuthenticationLoader, ErrorMessage, Loader, Radio } from '../../common';
import { isSigningWithIdCard } from '../../common/authenticationManager';
import { Shimmer } from '../../common/shimmer/Shimmer';
import { Recommended } from '../../common/Recommended';
import { usePageTitle } from '../../common/usePageTitle';
import { useSecondPillarPaymentRate } from './hooks';
import { PaymentRate } from './types';
import { useMandateDeadlines, useMe } from '../../common/apiHooks';
import { formatDateOn, formatDateYear } from '../../common/dateFormatter';
import { PaymentRateNudge } from '../../common/apiModels/nudge';
import { postPaymentRateRedirectDismissal } from '../../common/api';
import { trackNudgeEvent } from '../../common/nudge/tracking';
import styles from './SecondPillarPaymentRate.module.scss';

export const SecondPillarPaymentRate: React.FunctionComponent = () => {
  usePageTitle('pageTitle.secondPillarPayment');

  const { data: user } = useMe();
  const history = useHistory();
  const { pathname, search, state } = useLocation<{ nudge?: PaymentRateNudge } | undefined>();
  const [nudge] = useState(() => state?.nudge);
  const trackedView = useRef(false);

  const trackedEventData = (paymentRateNudge: PaymentRateNudge) => ({
    context: 'PAYMENT_RATE_REDIRECT',
    key: 'SECOND_PILLAR_PAYMENT_RATE',
    tag: 'nudge_payment_rate',
    path: pathname,
    channel: 'SCREEN',
    ...paymentRateNudge,
  });

  useEffect(() => {
    if (!nudge || trackedView.current) {
      return;
    }
    trackedView.current = true;
    history.replace({ pathname, search });
    trackNudgeEvent('NUDGE_VIEW', trackedEventData(nudge));
  }, [nudge]);

  const pendingPaymentRate =
    user?.secondPillarPaymentRates?.pending ?? user?.secondPillarPaymentRates?.current ?? null;

  const [paymentRate, setPaymentRate] = useState<PaymentRate | null>(6);

  const {
    changePaymentRate,
    cancelSigning,
    loading: signing,
    challengeCode,
    signedMandateId,
    paymentRateChangeMandateId,
    error,
    resetError,
  } = useSecondPillarPaymentRate();

  const { data: mandateDeadlines } = useMandateDeadlines();

  const dismissNudge = () => {
    postPaymentRateRedirectDismissal().catch(() => {});
    history.replace('/account');
  };

  const signPaymentRate = (rate: PaymentRate) => {
    if (nudge) {
      trackNudgeEvent('NUDGE_CLICK', trackedEventData(nudge));
    }
    changePaymentRate(rate);
  };

  if (!user) {
    return <Loader className="align-middle my-4" />;
  }

  if (signedMandateId && signedMandateId === paymentRateChangeMandateId) {
    return (
      <Redirect
        to={{
          pathname: '/2nd-pillar-payment-rate-success',
          state: {
            fulfillmentDate: mandateDeadlines?.paymentRateFulfillmentDate,
            paymentRate,
            isDecreased:
              paymentRate === 2 ||
              (paymentRate !== null &&
                pendingPaymentRate !== null &&
                paymentRate < pendingPaymentRate),
          },
        }}
      />
    );
  }

  // Simple logic: if current rate is 6, they can only decrease
  const isDecreasingScenario = pendingPaymentRate === 6;

  return (
    <div className="col-12 col-md-11 col-lg-8 mx-auto">
      {(signing || challengeCode) && (
        <AuthenticationLoader
          signingWithIdCard={isSigningWithIdCard()}
          controlCode={challengeCode}
          onCancel={cancelSigning}
          overlayed
        />
      )}

      {error && <ErrorMessage errors={error.body} onCancel={resetError} overlayed />}

      {nudge ? (
        <>
          <h1 className={`mb-3 ${styles.balancedHeading}`}>
            <FormattedMessage id="secondPillarPaymentRate.nudge.heading" />
          </h1>
          <p className="mb-3">
            <FormattedMessage id="secondPillarPaymentRate.nudge.lead" />
          </p>
          <p className="mb-3">
            <FormattedMessage id="secondPillarPaymentRate.nudge.body" />
          </p>
          <p className="mb-5">
            {!mandateDeadlines ? (
              <Shimmer height={24} />
            ) : (
              <>
                <b>
                  <FormattedMessage
                    id="secondPillarPaymentRate.nudge.deadline"
                    values={{ deadline: formatDateOn(mandateDeadlines.paymentRateDeadline) }}
                  />
                </b>{' '}
                <FormattedMessage
                  id="secondPillarPaymentRate.nudge.fulfillment"
                  values={{
                    fulfillmentDate: formatDateYear(mandateDeadlines.paymentRateFulfillmentDate),
                  }}
                />
              </>
            )}
          </p>
        </>
      ) : (
        <>
          <h1 className="mb-3">
            <FormattedMessage
              id={
                isDecreasingScenario
                  ? 'secondPillarPaymentRate.contributionChange.decrease'
                  : 'secondPillarPaymentRate.contributionChange.increase'
              }
            />
          </h1>
          <p className="mb-5">
            {!mandateDeadlines ? (
              <Shimmer height={24} />
            ) : (
              <FormattedMessage
                id={
                  isDecreasingScenario
                    ? 'secondPillarPaymentRate.applicationDeadline.decrease'
                    : 'secondPillarPaymentRate.applicationDeadline.increase'
                }
                values={{
                  paymentRateFulfillmentDate: formatDateYear(
                    mandateDeadlines?.paymentRateFulfillmentDate,
                  ),
                  a: (chunks: string) => <Link to="/2nd-pillar-tax-win">{chunks}</Link>,
                }}
              />
            )}
          </p>
        </>
      )}
      <p className="mb-4">
        <FormattedMessage id="secondPillarPaymentRate.chooseContributionRate" />
      </p>

      <div className="d-flex flex-column gap-2">
        <Radio
          name="payment-rate"
          id="payment-rate-2"
          selected={paymentRate === 2}
          onSelect={() => setPaymentRate(2)}
        >
          <p className="mb-1">
            <span className="fs-3 lh-sm fw-medium me-2">
              <FormattedMessage id="secondPillarPaymentRate.option.2Percent" />
            </span>
            {pendingPaymentRate === 2 && <Currently />}
          </p>
          <p className="m-0">
            <FormattedMessage
              id="secondPillarPaymentRate.calculation.2Percent"
              values={{ b: (chunks: string) => <b>{chunks}</b> }}
            />
          </p>
        </Radio>
        <Radio
          name="payment-rate"
          id="payment-rate-4"
          selected={paymentRate === 4}
          onSelect={() => setPaymentRate(4)}
        >
          <p className="mb-1">
            <span className="fs-3 lh-sm fw-medium me-2">
              <FormattedMessage id="secondPillarPaymentRate.option.4Percent" />
            </span>
            {pendingPaymentRate === 4 && <Currently />}
          </p>
          <p className="m-0">
            <FormattedMessage
              id="secondPillarPaymentRate.calculation.4Percent"
              values={{ b: (chunks: string) => <b>{chunks}</b> }}
            />
          </p>
        </Radio>
        <Radio
          name="payment-rate"
          id="payment-rate-6"
          selected={paymentRate === 6}
          onSelect={() => setPaymentRate(6)}
        >
          <p className="mb-1">
            <span className="fs-3 lh-sm fw-medium me-2">
              <FormattedMessage id="secondPillarPaymentRate.option.6Percent" />
            </span>
            <Recommended className="me-1" />
            {pendingPaymentRate === 6 && <Currently />}
          </p>
          <p className="mb-1">
            <FormattedMessage
              id="secondPillarPaymentRate.calculation.6Percent"
              values={{ b: (chunks: string) => <b>{chunks}</b> }}
            />
          </p>
          <p className="m-0 text-body-secondary">
            <FormattedMessage id="secondPillarPaymentRate.maximumBenefit" />
          </p>
        </Radio>
      </div>

      <div className="mt-5 d-flex flex-column-reverse flex-md-row justify-content-between">
        {nudge ? (
          <button type="button" className="btn btn-outline-primary mt-2" onClick={dismissNudge}>
            <FormattedMessage id="secondPillarPaymentRate.nudge.dismiss" />
          </button>
        ) : (
          <Link className="btn btn-light mt-2" to="/account">
            <FormattedMessage id="secondPillarPaymentRate.cancel" />
          </Link>
        )}
        <button
          type="button"
          className="btn btn-primary mt-2"
          disabled={!paymentRate || paymentRate === pendingPaymentRate}
          onClick={() => paymentRate && signPaymentRate(paymentRate)}
        >
          <FormattedMessage id="secondPillarPaymentRate.confirm.mandate.sign" />
        </button>
      </div>
    </div>
  );
};

const Currently = () => (
  <span className="badge rounded-pill text-bg-secondary align-text-bottom">
    <FormattedMessage id="secondPillarPaymentRate.current" />
  </span>
);
